import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppHeader from '@/components/ui/AppHeader';
import toast from 'react-hot-toast';

const PinSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [pin, setPin] = useState<string>('');
  const [confirmPin, setConfirmPin] = useState<string>('');
  const [step, setStep] = useState<'create' | 'confirm'>('create');
  
  const handleNumberClick = (num: string) => {
    const currentVal = step === 'create' ? pin : confirmPin;
    if (currentVal.length < 4) {
      if (step === 'create') setPin(prev => prev + num);
      else setConfirmPin(prev => prev + num);
    }
  };

  const handleDelete = () => {
    if (step === 'create') setPin(prev => prev.slice(0, -1));
    else setConfirmPin(prev => prev.slice(0, -1));
  };

  useEffect(() => {
    if (pin.length === 4 && step === 'create') {
      setTimeout(() => setStep('confirm'), 300);
    }

    if (confirmPin.length === 4 && step === 'confirm') {
      if (pin === confirmPin) {
        toast.success('Code PIN créé avec succès !');
        // Save logic would go here
        localStorage.setItem('leeris_user_pin', pin);
        setTimeout(() => navigate('/dashboard'), 1000);
      } else {
        toast.error('Les codes ne correspondent pas. Réessayez.');
        setConfirmPin('');
        setPin('');
        setStep('create');
      }
    }
  }, [pin, confirmPin, step, navigate]);

  const renderDots = (value: string) => (
    <div className="flex justify-center gap-4 mb-8">
      {[...Array(4)].map((_, i) => (
        <div
          key={i}
          className={`w-4 h-4 rounded-full border-2 border-primary ${
            i < value.length ? 'bg-primary' : 'bg-transparent'
          }`}
        />
      ))}
    </div>
  );

  return (
    <div className="relative flex min-h-screen w-full flex-col mx-auto max-w-[480px] bg-white dark:bg-background-dark">
      <AppHeader 
        title="Code PIN" 
        onBack={() => {
          if (step === 'confirm') {
            setStep('create');
            setConfirmPin('');
            setPin('');
          } else {
            navigate(-1);
          }
        }} 
      />

       <main className="flex-1 flex flex-col items-center px-6 pt-10">
            <div className="mb-8 text-center">
                 <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 text-primary">
                     <MaterialIcon icon="lock" className="text-3xl" />
                 </div>
                 <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                     {step === 'create' ? 'Créez votre code PIN' : 'Confirmez votre code PIN'}
                 </h1>
                 <p className="text-gray-500 dark:text-gray-400">
                     {step === 'create' 
                        ? 'Ce code vous permettra de vous connecter plus rapidement.' 
                        : 'Veuillez saisir à nouveau votre code pour confirmer.'}
                 </p>
            </div>

            {renderDots(step === 'create' ? pin : confirmPin)}

            <div className="mt-auto w-full pb-10">
                <div className="grid grid-cols-3 gap-y-6 gap-x-8 max-w-[280px] mx-auto">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                        <button
                            key={num}
                            onClick={() => handleNumberClick(num.toString())}
                            className="h-16 w-16 rounded-full text-2xl font-semibold text-slate-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
                        >
                            {num}
                        </button>
                    ))}
                    <div className="h-16 w-16" /> {/* Empty slot */}
                    <button
                         onClick={() => handleNumberClick('0')}
                         className="h-16 w-16 rounded-full text-2xl font-semibold text-slate-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
                    >
                        0
                    </button>
                     <button
                         onClick={handleDelete}
                         className="h-16 w-16 rounded-full text-slate-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
                    >
                        <MaterialIcon icon="backspace" className="text-xl" />
                    </button>
                </div>
            </div>
       </main>
    </div>
  );
};

export default PinSetupPage;
